<script setup lang="ts">
/**
 * 源站 HomeLobbyGameCard + Image：格子先铺站点 logo，
 * 进入视口后才绑真封面，失败留 logo。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  ORO_MX_SOURCE_PLACEHOLDER,
  oroMxShouldBindCover,
  oroMxShowCover,
} from '@/features/game/oro-mx-lobby-image';

const props = defineProps<{
  alt?: string;
  src: string;
}>();

const root = ref<HTMLElement | null>(null);
const inView = ref(false);
const loaded = ref(false);
const failed = ref(false);
let observer: IntersectionObserver | null = null;

const placeholder = ORO_MX_SOURCE_PLACEHOLDER;
const bindCover = computed(() => oroMxShouldBindCover(props.src, inView.value));
const showCover = computed(() => oroMxShowCover(bindCover.value, loaded.value, failed.value));

onMounted(() => {
  const el = root.value;
  if (!el) {
    inView.value = true;
    return;
  }
  if (typeof IntersectionObserver === 'undefined') {
    inView.value = true;
    return;
  }
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        inView.value = true;
        observer?.disconnect();
        observer = null;
      }
    },
    { rootMargin: '160px 80px', threshold: 0.01 },
  );
  observer.observe(el);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

watch(
  () => props.src,
  () => {
    loaded.value = false;
    failed.value = false;
  },
);
</script>

<template>
  <div ref="root" class="home_lobby_card_media">
    <img
      class="home_lobby_card_img home_lobby_card_img--default"
      :src="placeholder"
      alt=""
    />
    <img
      v-if="bindCover"
      class="home_lobby_card_img home_lobby_card_img--cover"
      :class="{ 'is-ready': showCover }"
      :src="src"
      :alt="alt ?? ''"
      decoding="async"
      @load="loaded = true"
      @error="failed = true"
    />
  </div>
</template>

<style scoped>
.home_lobby_card_media {
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1;
  background: #2e2e36;
  border-radius: 0.2667rem;
}

.home_lobby_card_img {
  display: block;
  width: 100%;
  height: 100%;
}

.home_lobby_card_img--default {
  object-fit: contain;
  object-position: 50% 50%;
  background: #2e2e36;
}

.home_lobby_card_img--cover {
  position: absolute;
  inset: 0;
  object-fit: fill;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.home_lobby_card_img--cover.is-ready {
  opacity: 1;
}
</style>
